import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { getFollowing } from '@/lib/follow';
import type { User } from '@/types/database';
import { FollowButton } from './FollowButton';

interface FollowingListProps {
  userId: string;
  onClose: () => void;
}

export function FollowingList({ userId, onClose }: FollowingListProps) {
  const navigate = useNavigate();
  const [following, setFollowing] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFollowing();
  }, [userId]);

  const loadFollowing = async () => {
    const data = await getFollowing(userId);
    setFollowing(data);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-scale-in">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Following</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-purple"></div>
            </div>
          ) : following.length > 0 ? (
            <div className="divide-y">
              {following.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <button
                    onClick={() => {
                      navigate(`/profile/${user.id}`);
                      onClose();
                    }}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    <img
                      src={user.avatar_url}
                      alt={user.full_name}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="font-medium truncate">{user.full_name}</p>
                      <p className="text-sm text-gray-500 truncate">
                        @{user.username}
                      </p>
                    </div>
                  </button>
                  <FollowButton
                    userId={user.id}
                    isFollowing={true}
                    onFollowChange={(isFollowing) => {
                      if (!isFollowing) {
                        setFollowing(prev => prev.filter(f => f.id !== user.id));
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              Not following anyone yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}